import { Button, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useLazyGetRecommendedUsersForMyProjectsQuery, useRecordRecommendationInteractionMutation } from '../../../api/recommendations'
import type { RecommendationCandidateUserMatch } from '../../../api/types'
import modalEmitter from '../../../components/ModalComponent/modalEmitter'
import { InviteProjectModalBody } from '../../../components/Modals'
import {
  RecommendationsLoadMoreTrigger,
  RecommendationResourceCard,
  RecommendationSection,
  RecommendationsEmpty,
  RecommendationsError,
  RecommendationsLoading,
} from './RecommendationCards'

const PAGE_SIZE = 20

function mergeMatches(base: RecommendationCandidateUserMatch[], next: RecommendationCandidateUserMatch[]): RecommendationCandidateUserMatch[] {
  const seen = new Set(base.map((match) => `${match.project?.id ?? 'no-project'}:${match.slot.id}:${match.user?.id ?? 'no-user'}`))
  return [...base, ...next.filter((match) => {
    const key = `${match.project?.id ?? 'no-project'}:${match.slot.id}:${match.user?.id ?? 'no-user'}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })]
}

export const RecommendationsForResourcesPage = () => {
  const [loadRecommendations, { isLoading, isFetching, isError }] = useLazyGetRecommendedUsersForMyProjectsQuery()
  const [recordInteraction] = useRecordRecommendationInteractionMutation()
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set())
  const [exactItems, setExactItems] = useState<RecommendationCandidateUserMatch[]>([])
  const [potentialItems, setPotentialItems] = useState<RecommendationCandidateUserMatch[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  const matchKey = (match: RecommendationCandidateUserMatch) => `${match.project?.id ?? 'no-project'}:${match.slot.id}:${match.user?.id ?? 'no-user'}`
  const hideMatch = (match: RecommendationCandidateUserMatch) => setHiddenKeys((current) => new Set(current).add(matchKey(match)))
  const restoreMatch = (match: RecommendationCandidateUserMatch) => {
    setHiddenKeys((current) => {
      const next = new Set(current)
      next.delete(matchKey(match))
      return next
    })
  }

  const loadPage = useCallback(async (cursor: string | null) => {
    const response = await loadRecommendations({ limit: PAGE_SIZE, cursor, kind: 'resource' }, true).unwrap()
    setExactItems((current) => cursor ? mergeMatches(current, response.exact) : response.exact)
    setPotentialItems((current) => cursor ? mergeMatches(current, response.potential) : response.potential)
    setNextCursor(response.meta?.next_cursor ?? null)
    setHasLoadedOnce(true)
  }, [loadRecommendations])

  useEffect(() => {
    void loadPage(null)
  }, [loadPage])

  const exact = exactItems.filter((match) => !hiddenKeys.has(matchKey(match)))
  const potential = potentialItems.filter((match) => !hiddenKeys.has(matchKey(match)))
  const isEmpty = exact.length === 0 && potential.length === 0

  const dismiss = async (match: RecommendationCandidateUserMatch) => {
    if (!match.project?.id || !match.user?.id) return

    hideMatch(match)
    try {
      await recordInteraction({
        direction: 'for_project',
        context_type: 'project',
        context_id: match.project.id,
        target_type: 'user',
        target_id: match.user.id,
        action: 'dismissed',
      }).unwrap()
    } catch {
      restoreMatch(match)
      toast.error('Не удалось скрыть рекомендацию')
    }
  }

  const act = (match: RecommendationCandidateUserMatch) => {
    if (!match.project?.id || !match.user?.id) return

    modalEmitter.emitOpenModal({
      dialog: {
        title: 'Обсудить ресурс',
        body: (
          <InviteProjectModalBody
            userId={match.user.id}
            projectId={match.project.id}
            projectName={match.project.name}
            introText={`Предложите обсудить ресурс "${match.slot.title}" для проекта.`}
            messagePlaceholder="Напишите, какой ресурс нужен и как удобно обсудить условия"
            submitLabel="Отправить предложение"
            successMessage="Предложение обсудить ресурс отправлено"
          />
        ),
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  if (isLoading || (isFetching && !hasLoadedOnce)) {
    return <RecommendationsLoading />
  }

  if (isError) {
    return <RecommendationsError />
  }

  return (
    <Stack gap={2.5}>
      {isEmpty ? (
        <RecommendationsEmpty message="Пока нет подходящих ресурсов для ваших проектов" />
      ) : (
        <>
          <RecommendationSection
            title="Точные совпадения"
            items={exact}
            renderItem={(match) => (
              <RecommendationResourceCard
                key={`exact-resource-${match.project?.id}-${match.user?.id}-${match.slot.id}`}
                match={match}
                isDismissing={!match.project || !match.user}
                onDismiss={() => dismiss(match)}
                primaryAction={
                  <Button
                    variant="follow"
                    size="small"
                    disabled={!match.project || !match.user}
                    onClick={() => act(match)}
                  >
                    Обсудить ресурс
                  </Button>
                }
              />
            )}
          />
          <RecommendationSection
            title="Могут подойти"
            items={potential}
            renderItem={(match) => (
              <RecommendationResourceCard
                key={`potential-resource-${match.project?.id}-${match.user?.id}-${match.slot.id}`}
                match={match}
                isDismissing={!match.project || !match.user}
                onDismiss={() => dismiss(match)}
                primaryAction={
                  <Button
                    variant="follow"
                    size="small"
                    disabled={!match.project || !match.user}
                    onClick={() => act(match)}
                  >
                    Обсудить ресурс
                  </Button>
                }
              />
            )}
          />
          <RecommendationsLoadMoreTrigger
            hasMore={Boolean(nextCursor)}
            isLoading={isFetching}
            onLoadMore={() => {
              if (nextCursor) void loadPage(nextCursor)
            }}
          />
        </>
      )}
    </Stack>
  )
}
